import { useState, useEffect, useCallback } from 'react';
import apiClient from './apiClient';
import analyticsService from './analyticsService';

const apiBaseURL = '/api/evaluations';

export interface CreateEvaluationRequest {
  conversation_id: string;
  message_id: string;
  feedback: 'positive' | 'negative';
  comment: string | null;
  question: string;
  response: string;
  citations: any[];
  metadata?: any;
}

export interface EvaluationResponse {
  id: string;
  feedback: string;
  comment: string | null;
  question: string;
  response: string;
  citations: any[];
  processed: boolean;
  created_at: string;
  admin_note?: string | null;
}

export interface SatisfactionMetrics {
  total: number;
  positive: number;
  negative: number;
  positive_rate: number;
  negative_rate: number;
}

export interface ChatbotMetrics {
  chatbot_id: string;
  chatbot_name: string;
  positive: number;
  negative: number;
  total: number;
  positive_rate: number;
}

// Service API
export const evaluationsService = {
  create: async (data: CreateEvaluationRequest): Promise<EvaluationResponse> => {
    const response = await apiClient.post(`${apiBaseURL}/`, data);
    return response.data;
  },

  getUnprocessed: async (): Promise<EvaluationResponse[]> => {
    const response = await apiClient.get(`${apiBaseURL}/unprocessed`);
    return response.data;
  },

  getProcessed: async (): Promise<EvaluationResponse[]> => {
    const response = await apiClient.get(`${apiBaseURL}/processed`);
    return response.data;
  },

  process: async (evaluationId: string, adminNote?: string): Promise<EvaluationResponse> => {
    const response = await apiClient.put(`${apiBaseURL}/${evaluationId}/process`, { 
      admin_note: adminNote 
    });
    return response.data;
  },

  getSatisfactionMetrics: async (days: number = 30): Promise<SatisfactionMetrics> => {
    const response = await apiClient.get(`${apiBaseURL}/metrics/satisfaction?days=${days}`);
    return response.data;
  },

  getChatbotMetrics: async (days: number = 30): Promise<{ chatbots: ChatbotMetrics[] }> => {
    const response = await apiClient.get(`${apiBaseURL}/metrics/chatbots?days=${days}`);
    return response.data;
  },

  getByMessageId: async (messageId: string): Promise<EvaluationResponse | null> => {
    const response = await apiClient.get(`${apiBaseURL}/by-message/${messageId}`);
    return response.data;
  },
};

// Custom hooks
export const useCreateEvaluation = () => {
  const [isPending, setIsPending] = useState(false);

  const mutateAsync = async (data: CreateEvaluationRequest) => {
    setIsPending(true);
    try {
      const result = await evaluationsService.create(data);
      return result;
    } finally {
      setIsPending(false);
    }
  };

  return { mutateAsync, isPending };
};

export const useUnprocessedEvaluations = () => {
  const [data, setData] = useState<EvaluationResponse[]>();
  const [isLoading, setIsLoading] = useState(true);

  const refetch = async () => {
    setIsLoading(true);
    try {
      const result = await evaluationsService.getUnprocessed();
      setData(result);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refetch();
  }, []);

  return { data, isLoading, refetch };
};

export const useProcessedEvaluations = () => {
  const [data, setData] = useState<EvaluationResponse[]>();
  const [isLoading, setIsLoading] = useState(true);

  const refetch = async () => {
    setIsLoading(true);
    try {
      const result = await evaluationsService.getProcessed();
      setData(result);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refetch();
  }, []);

  return { data, isLoading, refetch };
};

export const useProcessEvaluation = () => {
  const [isPending, setIsPending] = useState(false);

  const mutateAsync = async ({ 
    evaluationId, 
    adminNote 
  }: { 
    evaluationId: string; 
    adminNote?: string;
  }) => {
    setIsPending(true);
    try {
      const result = await evaluationsService.process(evaluationId, adminNote);
      return result;
    } finally {
      setIsPending(false);
    }
  };

  return { mutateAsync, isPending };
};

export const useMessageEvaluation = (messageId: string | null) => {
  const [data, setData] = useState<EvaluationResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const refetch = useCallback(async () => {
    if (!messageId) {
      setData(null);
      return;
    }

    setIsLoading(true);
    try {
      const result = await evaluationsService.getByMessageId(messageId);
      setData(result);
    } finally {
      setIsLoading(false);
    }
  }, [messageId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, isLoading, refetch };
};

export const useSatisfactionMetrics = (days: number = 30) => {
  const [data, setData] = useState<SatisfactionMetrics>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const result = await evaluationsService.getSatisfactionMetrics(days);
        setData(result);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [days]);

  return { data, isLoading };
};

export const useChatbotMetrics = (days: number = 30) => {
  const [data, setData] = useState<{ chatbots: ChatbotMetrics[] }>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const result = await evaluationsService.getChatbotMetrics(days);
        setData(result);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [days]);

  return { data, isLoading };
};

export const useRagUsageMetrics = (days: number = 7) => {
  const [data, setData] = useState<any>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const result = await analyticsService.getRagUsageMetrics(days);
        setData(result);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [days]);

  return { data, isLoading };
};

export const useIndexingStats = (days: number = 30) => {
  const [data, setData] = useState<any>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const result = await analyticsService.getIndexingStats(days);
        setData(result);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [days]);

  return { data, isLoading };
};
